/**
 * Lab Results Service
 * Handles FHIR R5 Observation resources for laboratory data
 */

import { toast } from "react-hot-toast";
import { FHIRObservation } from "../types/fhir";
import { LabResult } from "../types/fhir-health";
import { useI18nStore } from "../stores/i18nStore";
import { api } from "./api";
import { log } from "@/lib/log";

function extractErrorMessage(error: unknown, fallback: string): string {
  const anyError = error as any;
  const apiError = anyError?.response?.data?.error;

  if (typeof apiError === "string" && apiError.trim().length > 0) {
    return apiError;
  }

  if (apiError && typeof apiError === "object") {
    if (
      typeof (apiError as { message?: unknown }).message === "string" &&
      (apiError as { message: string }).message.trim().length > 0
    ) {
      return (apiError as { message: string }).message;
    }

    if (
      typeof (apiError as { code?: unknown }).code === "string" &&
      (apiError as { code: string }).code.trim().length > 0
    ) {
      return `Error ${(apiError as { code: string }).code}`;
    }
  }

  if (typeof anyError?.message === "string" && anyError.message.trim()) {
    return anyError.message;
  }

  return fallback;
}

// Common LOINC codes for laboratory tests including LDL Cholesterol
export const COMMON_LAB_TESTS = [
  {
    code: "18262-6",
    name: "LDL Cholesterol",
    unit: "mg/dL",
    category: "laboratory" as const,
    referenceRange: { low: 0, high: 100, unit: "mg/dL" },
  },
  {
    code: "2093-3",
    name: "Total Cholesterol",
    unit: "mg/dL",
    category: "laboratory" as const,
    referenceRange: { low: 150, high: 200, unit: "mg/dL" },
  },
  {
    code: "2085-9",
    name: "HDL Cholesterol",
    unit: "mg/dL",
    category: "laboratory" as const,
    referenceRange: { low: 35, high: 80, unit: "mg/dL" },
  },
  {
    code: "2571-8",
    name: "Triglycerides",
    unit: "mg/dL",
    category: "laboratory" as const,
    referenceRange: { low: 50, high: 150, unit: "mg/dL" },
  },
  {
    code: "33747-0",
    name: "Hemoglobin A1c",
    unit: "%",
    category: "laboratory" as const,
    referenceRange: { low: 4.0, high: 6.0, unit: "%" },
  },
  {
    code: "718-7",
    name: "Hemoglobin",
    unit: "g/dL",
    category: "laboratory" as const,
    referenceRange: { low: 12.0, high: 16.0, unit: "g/dL" },
  },
  {
    code: "6690-2",
    name: "White Blood Cell Count",
    unit: "K/uL",
    category: "laboratory" as const,
    referenceRange: { low: 4.0, high: 11.0, unit: "K/uL" },
  },
  {
    code: "777-3",
    name: "Platelet Count",
    unit: "K/uL",
    category: "laboratory" as const,
    referenceRange: { low: 150, high: 450, unit: "K/uL" },
  },
];

/**
 * Service for managing laboratory results with FHIR R5 compliance
 */
export const labResultsService = {
  /**
   * Save laboratory results as FHIR Observations
   */
  async saveLabResults(
    labResults: LabResult[],
  ): Promise<{ success: boolean; data?: unknown; error?: string }> {
    try {
      log.debug("🧪 Saving lab results to FHIR database:", labResults);

      // Get user ID from auth store
      const userId = JSON.parse(localStorage.getItem("auth-storage") || "{}")
        ?.state?.user?.id;
      if (!userId) {
        throw new Error("User not authenticated");
      }

      // Convert LabResult objects to FHIR Observation resources
      const fhirObservations = labResults.map((result) => {
        const observation: Record<string, unknown> = {
          resourceType: "Observation" as const,
          status: "final" as const,
          category: [
            {
              coding: [
                {
                  system:
                    "http://terminology.hl7.org/CodeSystem/observation-category",
                  code: "laboratory",
                  display: "Laboratory",
                },
              ],
            },
          ],
          code: {
            coding: [
              {
                system: "http://loinc.org",
                code: result.code,
                display: result.name,
              },
            ],
          },
          subject: {
            reference: `Patient/${userId}`,
          },
          effectiveDateTime: result.effectiveDateTime,
          issued: new Date().toISOString(),
          meta: {
            profile: ["http://hl7.org/fhir/StructureDefinition/Observation"],
            lastUpdated: new Date().toISOString(),
          },
        };

        // Add valueQuantity only if it exists
        if (result.valueQuantity) {
          observation.valueQuantity = {
            value: result.valueQuantity.value,
            unit: result.valueQuantity.unit,
            system: result.valueQuantity.system,
            code: result.valueQuantity.unit,
          };
        }

        // Add valueString only if it exists
        if (result.valueString) {
          observation.valueString = result.valueString;
        }

        return observation;
      });

      // Send each observation to the existing singular backend endpoint.
      const savedObservations = await Promise.all(
        fhirObservations.map(async (observation) => {
          const response = await api.post("/fhir/observation", observation);
          return response.data;
        }),
      );

      log.debug("✅ Lab results saved successfully:", savedObservations);

      toast.success(useI18nStore.getState().t.hrpLabResultsSaved);

      return {
        success: true,
        data: savedObservations,
      };
    } catch (error: any) {
      console.error("❌ Error saving lab results:", error);

      const errorMessage = extractErrorMessage(
        error,
        useI18nStore.getState().t.hrpErrorSavingLabResults,
      );
      toast.error(errorMessage);

      return {
        success: false,
        error: errorMessage,
      };
    }
  },

  /**
   * Retrieve laboratory results from FHIR database
   */
  async getLabResults(): Promise<{
    success: boolean;
    data?: LabResult[];
    error?: string;
  }> {
    try {
      log.debug("📊 Fetching lab results from FHIR database");

      const response = await api.get("/fhir/Observation");
      const entries: Array<{ fullUrl?: string; resource: FHIRObservation }> =
        response.data.entry || [];
      const knownLabCodes = new Set(COMMON_LAB_TESTS.map((test) => test.code));

      // Convert FHIR Observations back to LabResult objects
      const labResults: LabResult[] = entries
        .filter((entry) => {
          const obs = entry.resource;
          const code = obs.code?.coding?.[0]?.code || "";
          const hasLabCategory = obs.category?.some((cat) =>
            cat.coding?.some((coding) => coding.code === "laboratory"),
          );

          // Keep classic laboratory-tagged observations and support known lab LOINC fallback.
          return (
            !!obs.effectiveDateTime &&
            (hasLabCategory || knownLabCodes.has(code))
          );
        })
        .map((entry) => {
          const obs = entry.resource;
          const fullUrlId = entry.fullUrl?.split("/").pop() || "";
          const result: LabResult = {
            id: obs.id || fullUrlId,
            code: obs.code?.coding?.[0]?.code || "",
            name: obs.code?.coding?.[0]?.display || "",
            category: "laboratory" as const,
            effectiveDateTime: obs.effectiveDateTime!,
          };

          // Add performer if available
          if (obs.subject?.reference) {
            result.performer = obs.subject.reference;
          }

          // Add optional fields only if they exist
          if (obs.valueQuantity && obs.valueQuantity.value !== undefined) {
            result.valueQuantity = {
              value: obs.valueQuantity.value,
              unit: obs.valueQuantity.unit || "",
              system: "http://unitsofmeasure.org" as const,
            };
          }

          if (obs.valueString) {
            result.valueString = obs.valueString;
          }

          // Add interpretation if available (default to normal)
          result.interpretation = "normal" as const;

          return result;
        });

      log.debug("✅ Lab results fetched successfully:", labResults);

      return {
        success: true,
        data: labResults,
      };
    } catch (error: any) {
      console.error("❌ Error fetching lab results:", error);

      const errorMessage = extractErrorMessage(
        error,
        useI18nStore.getState().t.hrpErrorLoadingLabResults,
      );

      return {
        success: false,
        error: errorMessage,
      };
    }
  },

  /**
   * Upload PDF lab report and link to observation
   */
  async uploadLabReport(
    file: File,
    labResultId?: string,
  ): Promise<{ success: boolean; data?: unknown; error?: string }> {
    try {
      log.debug("📄 Uploading lab report PDF:", file.name);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", "lab-report");
      formData.append("description", `Lab Report: ${file.name}`);

      if (labResultId) {
        formData.append("linkedObservationId", labResultId);
      }

      const response = await api.post("/documents/upload", formData);

      log.debug("✅ Lab report uploaded successfully:", response.data);

      toast.success(useI18nStore.getState().t.hrpLabReportUploaded);

      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error("❌ Error uploading lab report:", error);

      const errorMessage = extractErrorMessage(
        error,
        useI18nStore.getState().t.hrpErrorUploadingLabReport,
      );
      toast.error(errorMessage);

      return {
        success: false,
        error: errorMessage,
      };
    }
  },
};
